import type { Manifest } from '@api/views';
import { launcherBridge, launcherEvents, type Unlisten } from './is-launcher';

/**
 * The Rust side, with names and types instead of strings.
 *
 * Every command used to be reachable only as `invoke('resume_download', {
 * productId })` — a string the compiler cannot check against a signature it
 * cannot see, in a file that had never been run. A typo in the command name, a
 * missing argument or an argument spelled the way Rust spells it are all the
 * same failure: a rejected promise at the moment a player presses a button.
 *
 * So there is one module that knows those strings, and it is this one. Every
 * screen calls a function.
 *
 * ## Two directions now
 *
 * Commands go one way and **events come back**. That is new, and it is what
 * moving the download loop into Rust bought: a 40GB build is about a hundred
 * and sixty thousand chunks, and when this side drove the loop that was a
 * hundred and sixty thousand `invoke` calls, each returning a status object —
 * the bridge became the bottleneck for the thing it was reporting on. One call
 * starts a download now, and `onDownloadProgress` receives it about four times
 * a second.
 *
 * ## The two casings, and why both are correct
 *
 * **Command arguments are camelCase.** `#[tauri::command]` renames a function's
 * parameters for the JavaScript side, so Rust's `product_id` is passed as
 * `productId`. That is Tauri's convention and not ours.
 *
 * **Struct fields are snake_case.** `StartDownloadArgs`, `DownloadProgress`,
 * `InstallRecord`, `CachedEntitlement` and `PlaySession` all derive serde's
 * default with no rename attribute, so their JSON *is* Rust's spelling. Event
 * payloads are the same, because an event is a serialised struct with no
 * parameter renaming anywhere near it. The same rule governs
 * `LauncherEntitlement` in `apps/api/src/lib/builds/launcher`, and the note
 * there is worth repeating: renaming a field on this side does not fail a
 * build. It makes the value arrive as `undefined`, or deserialise to the
 * struct's default on the Rust side, which for a boolean gate means the gate
 * silently opens.
 *
 * Mirroring rather than generating is a deliberate repeat of the decision
 * `publish.ts` made about `Manifest`: the Rust struct is the definition, this
 * is a description of its JSON, and a generated binding would be a third thing
 * to keep in step.
 */

export class LauncherIpcError extends Error {
  override readonly name = 'LauncherIpcError';
}

/* -------------------------------------------------------------------------- */
/* The wire types, field for field with main.rs                               */
/* -------------------------------------------------------------------------- */

/**
 * `DownloadJob::state`, lowercased by the Rust side.
 *
 * `stalled` is the one worth reading twice: every server failed, the install is
 * **not** damaged, and starting the download again picks up where it stopped —
 * every chunk that landed is on disk. It is a retryable state rather than an
 * error, which is why the screen offers a button and not an apology.
 */
export type DownloadState = 'queued' | 'running' | 'paused' | 'complete' | 'stalled';

/** One entry of the ranked list `GET /v1/launcher/servers` hands back. */
export interface ContentServer {
  id: string;
  base_url: string;
  /** Platform hint, lower is preferred. `servers.rs` overrides it by measurement. */
  priority: number;
}

/**
 * Everything the Rust side needs to take a title from nothing to playable.
 *
 * **One call for a whole build.** This used to be the first of a hundred and
 * sixty thousand: `start_download` planned, and then this side called
 * `fetch_chunk` once per chunk over the IPC bridge. The loop lives in
 * `downloader.rs` now, and what comes back here is `download-progress`.
 */
export interface StartDownloadArgs {
  product_id: string;
  /**
   * What the title is called, for the folder it installs into.
   *
   * Sent rather than the folder name itself, so that the rule turning a title
   * into a filesystem-safe path component — which characters Windows refuses,
   * which device names cannot exist, where a trailing dot goes — lives in
   * `launcher-core` with a test on it rather than being a second opinion here.
   */
  product_name: string;
  build_id: string;
  version: string;
  manifest: Manifest;
  /**
   * Hash to size in bytes, from the index the platform publishes beside a
   * manifest — and still `{}`, because no endpoint returns it yet. That costs
   * the *estimate* and nothing else: `bytes_downloaded` is counted as bytes
   * arrive and is right either way, while `bytes_to_fetch` stays zero until the
   * platform publishes sizes.
   */
  chunk_sizes: Record<string, number>;
  servers: ContentServer[];
  /**
   * Proof the caller may fetch this title's chunks, valid for six hours.
   *
   * Held by the Rust side for the length of the download and replaced through
   * `resumeDownload` when it expires — which a large download on a slow line is
   * expected to see rather than being an exceptional case.
   */
  entitlement_token: string;
  /**
   * The player's games directory — the *parent*, not this game's own folder.
   *
   * The launcher's own folder when omitted, and when the path is relative: a
   * relative one would resolve against whatever directory the launcher was
   * started from, which differs between a shortcut, a terminal and the Run box.
   */
  library_root?: string;
  /** Chunks in flight at once. Four when omitted. */
  concurrency?: number;
  /** Bytes per second, or omitted for unlimited. */
  bandwidth_cap_bps?: number;
}

/** What `start_download` answers with, before a byte moves. */
export interface StartedPlan {
  product_id: string;
  chunks_total: number;
  chunks_needed: number;
  /** Zero until the platform publishes chunk sizes. See `chunk_sizes`. */
  bytes_to_fetch: number;
  bytes_total: number;
  /** Share of the build already in the local chunk store, 0 to 1. */
  reuse_ratio: number;
  install_root: string;
  is_update: boolean;
}

/** `InstallRecord` in `launcher-core`, field for field. */
export interface InstallRecord {
  product_id: string;
  build_id: string;
  version: string;
  /**
   * The build's content address, and what an update check compares.
   *
   * A digest rather than the version string, deliberately: a version is what a
   * studio chose to call the build, so comparing digests catches a build
   * republished under an unchanged version and comparing versions does not.
   */
  manifest_digest: string;
  install_root: string;
  installed_at: number;
  /** Serde's internally-tagged `InstallState`. */
  state: { state: 'ready' } | { state: 'applying'; target_digest: string };
  /**
   * Whether this build says how to start itself on this machine.
   *
   * What the Play button is drawn from. Recorded when the build was applied
   * rather than derived on demand: deriving it means parsing the manifest,
   * which for a large build is tens of megabytes of JSON per row per render.
   * False for a build published before manifests carried a launch
   * configuration, and for a record written before this field existed.
   */
  launchable: boolean;
}

export interface InstalledTitle {
  record: InstallRecord | null;
  update_available: boolean;
  /**
   * An apply was interrupted. Finished by calling `startDownload` again: every
   * chunk is already on disk, so the plan is empty and the job goes straight to
   * writing files.
   */
  needs_apply: boolean;
  /**
   * Whether the installed build says how to start itself on this platform.
   *
   * False for every build published before manifests carried a launch
   * configuration. A screen that offered Play regardless would be offering a
   * button that answers with an apology.
   */
  launchable: boolean;
  /** Whether this launcher currently has it running. */
  running: boolean;
}

/** `DeviceCredential` in the shell, field for field. */
export interface DeviceCredential {
  device_id: string;
  /** Issued once by the API and stored by the shell. */
  token: string;
  name: string;
}

export interface StoreSize {
  chunks: number;
  bytes: number;
}

/** `CachedEntitlement` in `launcher-core`, and `LauncherEntitlement` in core. */
export interface CachedEntitlement {
  product_id: string;
  product_name: string;
  cached_at: number;
  expires_at: number | null;
  requires_nda: boolean;
  nda_accepted: boolean;
  pinned_version: string | null;
}

export interface VerifyResponse {
  playable: boolean;
  missing: number;
  corrupt: number;
  verified: number;
  total: number;
}

export interface LaunchResponse {
  allowed: boolean;
  /**
   * Set when the offline entitlement cache is past its grace period and the
   * game is playable anyway. Hard rule 19: a game is never revoked as a
   * penalty, and "we could not reach the server" is not a reason to take one
   * away — so this is a sentence to show, not a block.
   */
  stale_days: number | null;
  /** `BlockReason`, debug-formatted. Null whenever `allowed` is true. */
  blocked_reason: string | null;
}

/**
 * `SocialSignIn` in main.rs, field for field.
 *
 * **There is no token here, and that absence is the design.** The Rust side
 * hands back a `code` that is single-use and dead in sixty seconds; the session
 * itself is fetched by `POST /v1/launcher/oauth/exchange`, in a response body,
 * so it never travels through the loopback URL that a browser writes into its
 * history and syncs to every other machine somebody is signed into.
 *
 * Exactly one of `code` and `error` is set. `error` is a machine code from
 * Better Auth's vocabulary — `account_not_linked`, `access_denied` — and
 * `describeProviderError` in `auth.ts` is what turns it into a sentence, the
 * same map the browser flow reads.
 */
export interface SocialSignIn {
  state: string;
  code: string | null;
  error: string | null;
}

export interface PlaySession {
  product_id: string;
  build_version: string;
  /** Wall clock. Diagnostics only; never satisfies a refund threshold. */
  elapsed_minutes: number;
  /** Minutes with input or progression. The real measure (section 5.4). */
  active_minutes: number;
  input_events: number;
  progression_events: number;
  crashed: boolean;
  idempotency_key: string;
}

/* -------------------------------------------------------------------------- */
/* The bridge                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * One call, with the two failures a caller cannot do anything about turned into
 * one error type it can.
 *
 * **No bridge** means a browser reached a launcher screen. That should be
 * impossible — the routes are registered only when `isLauncher()` is true and
 * each screen refuses on its own as well — so this throws rather than returning
 * a plausible empty result, because a silent no-op here would hide the routing
 * mistake that produced it.
 *
 * **A rejected invoke** carries the `Err(String)` the command returned, which
 * in main.rs is always a sentence written for a person: *No download is running
 * for that product.* Those are shown as-is. Anything else is stringified rather
 * than swallowed, so an unexpected shape is still legible in the one place a
 * player can see it.
 */
async function call<T>(command: string, args: Record<string, unknown> = {}): Promise<T> {
  const invoke = launcherBridge();

  if (!invoke) {
    throw new LauncherIpcError(
      `The launcher bridge is not here, so \`${command}\` cannot run. ` +
        'This screen belongs to the desktop launcher.',
    );
  }

  try {
    return (await invoke(command, args)) as T;
  } catch (cause) {
    throw new LauncherIpcError(
      typeof cause === 'string' ? cause : ((cause as Error)?.message ?? String(cause)),
    );
  }
}

/**
 * Plans a download, starts it, and answers with the plan.
 *
 * The plan first is the point: a player on a metered connection deciding
 * whether to start a 40GB download needs the number before the first chunk, not
 * after it. Everything after that arrives as events — see `onDownloadProgress`.
 *
 * The parameter is named `args` in Rust and takes a whole struct, so the
 * payload is `{ args: … }` and the struct's own fields keep Rust's spelling.
 * That is the one command here where both casings appear in the same call.
 *
 * **Also how an interrupted apply is finished, and how a stalled download is
 * retried.** Both are the same operation: re-plan against the chunk store,
 * which already holds everything that landed, and do whatever is left. There is
 * no separate repair path, which is what keeps this one exercised.
 */
export function startDownload(args: StartDownloadArgs): Promise<StartedPlan> {
  return call<StartedPlan>('start_download', { args });
}

/**
 * Stops handing out new work.
 *
 * Whatever is already in flight finishes rather than being cancelled and
 * re-fetched later: the workers park between chunks, never mid-request.
 */
export function pauseDownload(productId: string): Promise<void> {
  return call<void>('pause_download', { productId });
}

/**
 * Wakes a paused download, optionally with a fresh entitlement token.
 *
 * The token is what `download-token-expired` asks for. **A stalled download is
 * not resumed with this** — its workers have already exited. Call
 * `startDownload` again instead, which re-reads the chunk store, counts
 * everything that did land and fetches only the remainder.
 */
export function resumeDownload(productId: string, entitlementToken?: string): Promise<void> {
  return call<void>('resume_download', { productId, entitlementToken });
}

/**
 * Abandons a download, keeping the chunks that already landed.
 *
 * They are content-addressed, so they are worth exactly as much to the next
 * attempt — or to another title that happens to share them — as they were to
 * this one. Deleting them would make cancelling cost a player everything the
 * download had achieved.
 */
export function cancelDownload(productId: string): Promise<void> {
  return call<void>('cancel_download', { productId });
}

/** Every title installed on this machine. */
export function installedTitles(): Promise<InstallRecord[]> {
  return call<InstallRecord[]>('installed_titles');
}

/**
 * Whether one title needs installing, updating or finishing.
 *
 * `manifestDigest` is what the platform currently serves for the title, from
 * `InstallTarget`. Passing it rather than having Rust fetch it keeps this
 * offline-safe: "is an apply half-finished" is answerable with no network,
 * which is exactly the state a machine that lost power wakes up in.
 */
export function installedTitle(
  productId: string,
  manifestDigest?: string,
): Promise<InstalledTitle> {
  return call<InstalledTitle>('installed_title', { productId, manifestDigest });
}

/**
 * Verifies an install by content. The Repair button.
 *
 * **Callable at last.** This took a map of what is on disk and the shell had no
 * disk, so nothing ever called it. It takes a product id now and the Rust side
 * builds that map itself, by re-chunking the installed files with the
 * manifest's own chunker configuration — the boundaries are a deterministic
 * function of the content, so a correct file re-chunks to the addresses the
 * manifest lists and a damaged one does not.
 */
export function verify(productId: string): Promise<VerifyResponse> {
  return call<VerifyResponse>('verify', { productId });
}

/**
 * Deletes an installed game's files.
 *
 * The shared chunk store is left alone: its contents are shared across titles
 * and versions, so dropping this title's chunks would slow the next install of
 * anything on the same engine. `pruneStore` is what reclaims that space.
 */
export function uninstall(productId: string): Promise<void> {
  return call<void>('uninstall', { productId });
}

/** How much disk the shared chunk store is using. */
export function storeSize(): Promise<StoreSize> {
  return call<StoreSize>('store_size');
}

/**
 * Brings the chunk store back under a cap, returning the bytes reclaimed.
 *
 * Chunks a running download still needs are pinned and never evicted — without
 * that, a cap smaller than the build in flight would make each arriving chunk
 * delete one the same job needs, and the download would never finish.
 */
export function pruneStore(capBytes: number): Promise<number> {
  return call<number>('prune_store', { capBytes });
}

/* -------------------------------------------------------------------------- */
/* What the Rust side says without being asked                                */
/* -------------------------------------------------------------------------- */

export interface DownloadProgress {
  product_id: string;
  state: DownloadState;
  /**
   * 0 to 1, measured against chunks that need fetching rather than build size.
   * A 40GB game patching 200MB fills the bar as the 200MB arrives instead of
   * sitting at 99.5%.
   */
  progress: number;
  chunks_remaining: number;
  chunks_total: number;
  /** Bytes that actually crossed the wire this run, compressed. */
  bytes_downloaded: number;
  /** Zero until the platform publishes chunk sizes. */
  bytes_to_fetch: number;
  reuse_ratio: number;
  /**
   * Observed over the last reporting interval rather than over the whole
   * download, because what a player wants to know is how fast it is going now —
   * an average over the last hour hides a connection that has just died.
   */
  bytes_per_second: number;
}

export interface InstallProgress {
  product_id: string;
  bytes_written: number;
  bytes_total: number;
}

export interface InstalledEvent {
  product_id: string;
  build_id: string;
  version: string;
  install_root: string;
  files_written: number;
  files_skipped: number;
  files_removed: number;
  bytes_written: number;
}

export interface DownloadFailed {
  product_id: string;
  message: string;
  retryable: boolean;
}

export interface TokenExpired {
  product_id: string;
}

/**
 * One subscription, with the no-bridge case turned into a no-op.
 *
 * **Not the throw that `call` uses**, and the difference is deliberate. A
 * command is something a person pressed, so failing loudly is right. A
 * subscription is set up in an effect on mount, where throwing would break the
 * render of a screen that is otherwise fine — and a browser that reached a
 * launcher screen has nothing to receive anyway.
 *
 * The returned function is safe to call whether or not the subscription was
 * ever established, which is what lets an effect's cleanup be unconditional.
 */
function subscribe<T>(event: string, handler: (payload: T) => void): () => void {
  const listen = launcherEvents();
  if (!listen) return () => {};

  let stop: Unlisten | null = null;
  let cancelled = false;

  void listen<T>(event, ({ payload }) => handler(payload)).then((unlisten) => {
    /*
     * The effect may have been cleaned up before `listen` resolved — a fast
     * navigation, or React's development double-invoke of every effect. Without
     * this the subscription outlives the screen that made it and leaks one
     * handler per visit, each still firing four times a second.
     */
    if (cancelled) {
      unlisten();
      return;
    }
    stop = unlisten;
  });

  return () => {
    cancelled = true;
    stop?.();
  };
}

/** Progress, emitted about four times a second while a download runs. */
export function onDownloadProgress(handler: (progress: DownloadProgress) => void): () => void {
  return subscribe<DownloadProgress>('download-progress', handler);
}

/**
 * The entitlement token expired and the download has parked.
 *
 * Mint another and hand it over with `resumeDownload`. Nothing is re-fetched:
 * the chunk that was in flight went back on the queue before this fired.
 */
export function onDownloadTokenExpired(handler: (event: TokenExpired) => void): () => void {
  return subscribe<TokenExpired>('download-token-expired', handler);
}

/** Files are being written. The apply is not instant for a large build. */
export function onInstallProgress(handler: (progress: InstallProgress) => void): () => void {
  return subscribe<InstallProgress>('install-progress', handler);
}

/** The build is on disk and playable. */
export function onInstalled(handler: (event: InstalledEvent) => void): () => void {
  return subscribe<InstalledEvent>('download-installed', handler);
}

/** The job stopped without installing. Retryable unless it says otherwise. */
export function onDownloadFailed(handler: (event: DownloadFailed) => void): () => void {
  return subscribe<DownloadFailed>('download-failed', handler);
}

/* -------------------------------------------------------------------------- */
/* Playing                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Starts an installed game.
 *
 * Rejects with a sentence when the build cannot be started — it never said how,
 * it has nothing for this platform, its executable points outside the game
 * folder, or the file it names is not one of its own. All four are decided in
 * `launcher-core` before a process is spawned.
 *
 * **Check `canStart` first.** This does not consult the entitlement cache; that
 * is a separate decision with its own rules about staleness and NDAs, and
 * folding it in here would put hard rule 19 somewhere a screen could skip it.
 */
export function launch(productId: string): Promise<void> {
  return call<void>('launch', { productId });
}

/** Asks a running game to close. */
export function stopGame(productId: string): Promise<void> {
  return call<void>('stop_game', { productId });
}

/**
 * Which titles this launcher currently has running.
 *
 * Read on mount, so a screen navigated away from and back comes up showing Stop
 * rather than Play. The launcher is the only thing that knows: the game is its
 * child process.
 */
export function runningGames(): Promise<string[]> {
  return call<string[]>('running_games');
}

/**
 * Sessions recorded but not yet accepted by the platform.
 *
 * **What makes playtime survive being offline.** A session is written to disk
 * the moment the game exits and cleared only when the API has taken it, so a
 * game played on a train reports on the next run — under the same idempotency
 * key, which is what stops one session becoming four.
 */
export function pendingSessions(): Promise<PlaySession[]> {
  return call<PlaySession[]>('pending_sessions');
}

/** Forgets a session the platform has accepted. Never call this on a failure. */
export function sessionReported(idempotencyKey: string): Promise<void> {
  return call<void>('session_reported', { idempotencyKey });
}

export interface GameStarted {
  product_id: string;
  version: string;
}

export interface GameExited {
  product_id: string;
  /** What to POST to `/v1/launcher/sessions`. */
  session: PlaySession;
  crashed: boolean;
}

export function onGameStarted(handler: (event: GameStarted) => void): () => void {
  return subscribe<GameStarted>('game-started', handler);
}

/** A game exited, with the session to report. */
export function onGameExited(handler: (event: GameExited) => void): () => void {
  return subscribe<GameExited>('game-exited', handler);
}

/* -------------------------------------------------------------------------- */
/* This machine                                                               */
/* -------------------------------------------------------------------------- */

/**
 * This launcher's device registration, if it has one.
 *
 * Held by the Rust side rather than in `localStorage`, deliberately. Browser
 * storage is cleared by the things that clear browser storage, and each time it
 * is the launcher would register again and leave another row in the player's
 * machines list — a list that accumulates a row per storage clear is one nobody
 * can revoke from, which defeats the reason it exists.
 */
export function deviceCredential(): Promise<DeviceCredential | null> {
  return call<DeviceCredential | null>('device_credential');
}

/** Stores the registration the API just issued. The token is shown only once. */
export function storeDeviceCredential(credential: DeviceCredential): Promise<void> {
  return call<void>('store_device_credential', { credential });
}

/**
 * Forgets this launcher's registration, locally.
 *
 * Revoke the row at the API *first*. A launcher that forgot the token before
 * revoking it would leave a live credential on the account with nothing able to
 * name it.
 */
export function forgetDeviceCredential(): Promise<void> {
  return call<void>('forget_device_credential');
}

/** A name for this machine, for the list the player revokes from. */
export function suggestedDeviceName(): Promise<string> {
  return call<string>('suggested_device_name');
}

/* -------------------------------------------------------------------------- */
/* Where games live                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The folder games install under when nobody has picked one.
 *
 * Shown on the settings screen so the control says something before it has been
 * used — a path a player can read beats the word "default", which tells
 * somebody hunting for their game files nothing.
 */
export function defaultLibraryRoot(): Promise<string> {
  return call<string>('default_library_root');
}

/**
 * Opens the native folder picker.
 *
 * **Null means the player cancelled**, which is an ordinary outcome rather than
 * a failure: pressing Escape should leave the setting as it was, and a rejected
 * promise would put a message on screen about a decision somebody deliberately
 * did not make.
 *
 * Rejects when the chosen folder cannot be written to. That check happens in
 * Rust, by writing a file: a picker will happily return `C:\Program Files` or a
 * share that has gone read-only, and every one of those otherwise fails after a
 * 40GB download, at the moment the first file is written.
 */
export function chooseLibraryRoot(): Promise<string | null> {
  return call<string | null>('choose_library_root');
}

/** Whether a game may start. See `LaunchResponse` on why staleness is not a block. */
export function canStart(
  entitlement: CachedEntitlement,
  installedVersion: string | null,
  nowUnix: number,
): Promise<LaunchResponse> {
  return call<LaunchResponse>('can_start', { entitlement, installedVersion, nowUnix });
}

export function sessionStart(productId: string, buildVersion: string): Promise<void> {
  return call<void>('session_start', { productId, buildVersion });
}

/**
 * Reports activity in the current minute.
 *
 * `monotonicSeconds` must come from a monotonic source — `performance.now()`
 * divided by a thousand, never `Date.now()`. Section 5.4: playtime gates
 * refunds, so a wall clock here makes changing a system setting a way to get
 * free games, and NTP correcting a drifting clock mid-session would do it by
 * accident.
 */
export function sessionTick(
  productId: string,
  monotonicSeconds: number,
  inputEvents: number,
  progressionEvents: number,
): Promise<void> {
  return call<void>('session_tick', {
    productId,
    monotonicSeconds,
    inputEvents,
    progressionEvents,
  });
}

/**
 * Finishes a session and hands it back for reporting.
 *
 * The idempotency key is the caller's and must be persisted with the session
 * before any upload is attempted: a session held on disk while the player is
 * offline has to report under the same key whenever it gets through, and a
 * fresh key per attempt is how one session becomes four.
 */
export function sessionFinish(productId: string, idempotencyKey: string): Promise<PlaySession> {
  return call<PlaySession>('session_finish', { productId, idempotencyKey });
}

/**
 * Runs one social sign-in through the system browser and waits for the answer.
 *
 * **This is the long one.** It binds a loopback listener, opens the operating
 * system's browser, and does not resolve until the flow comes back or five
 * minutes pass — so the button that calls it has to stay in a busy state for
 * however long somebody spends at a consent screen, and there is no progress to
 * report in between. That is the shape of every RFC 8252 sign-in and not
 * something this side can improve on.
 *
 * `apiBase` is passed in rather than compiled into the Rust side because
 * `VITE_API_URL` is where this app's API address already lives, and two places
 * holding it is two places that can disagree — with the symptom being a
 * launcher that opens a browser at a dev API in a shipped build. The Rust side
 * still refuses anything that is not https, or http on loopback, so passing it
 * from here widens nothing.
 *
 * Rejects with `LauncherIpcError` for the failures that are about this machine:
 * no browser could be opened, the port could not be bound, nothing came back in
 * time. A provider saying no is **not** one of those — it resolves, with
 * `error` set.
 */
export function startSocialSignIn(provider: string, apiBase: string): Promise<SocialSignIn> {
  return call<SocialSignIn>('start_social_sign_in', { provider, apiBase });
}

/* -------------------------------------------------------------------------- */
/* The launcher updating itself                                               */
/* -------------------------------------------------------------------------- */

/**
 * A newer launcher exists.
 *
 * Field for field with `UpdateAvailable` in `apps/launcher/src-tauri/src/
 * updater.rs`, snake_case included — see the note at the top of this file on
 * why event payloads keep Rust's spelling.
 *
 * `notes` and `date` are whatever the release manifest carried. Both are
 * written by `tools/release-launcher.mjs` and both are optional in the format,
 * so a screen that renders them has to survive their absence.
 */
export interface UpdateAvailable {
  version: string;
  current_version: string;
  notes: string | null;
  date: string | null;
}

/**
 * Download progress for the launcher's own update.
 *
 * **`total_bytes` can be null** and a progress bar here has to cope, which is
 * the one way this differs from `DownloadProgress`. A game download knows its
 * size before it starts because the manifest says so; this one knows only what
 * `Content-Length` said, and a server that omitted it leaves a real download
 * with no denominator.
 */
export interface UpdateProgress {
  downloaded_bytes: number;
  total_bytes: number | null;
}

/** Why no update happened. The launcher is still running and still usable. */
export interface UpdateFailed {
  message: string;
}

/**
 * Whether a newer launcher exists, without doing anything about it.
 *
 * Resolves `null` when the running version is current. Rejects when the feed
 * could not be reached or did not parse — which a settings screen should treat
 * as "could not check" rather than "up to date", because those are different
 * sentences and only one of them is a reason to try again.
 */
export function checkForUpdate(): Promise<UpdateAvailable | null> {
  return call<UpdateAvailable | null>('check_for_update');
}

/**
 * Applies an update now.
 *
 * **This closes the launcher.** On Windows the update is the installer, and it
 * cannot replace an executable that is still running — so the process exits
 * partway through this call and the promise never settles. Say so on screen
 * before calling it.
 *
 * Rarely needed: `updater.rs` already applies whatever the feed is offering a
 * few seconds after startup. This is the button for somebody who does not want
 * to wait for the next launch.
 */
export function installUpdate(): Promise<void> {
  return call<void>('install_update');
}

/**
 * An update was found and is about to be downloaded.
 *
 * The launcher does this to itself at startup, so this fires without anything
 * having asked. It is the moment to say what is happening — the next thing the
 * player sees otherwise is the application closing.
 */
export function onUpdateAvailable(handler: (update: UpdateAvailable) => void): () => void {
  return subscribe<UpdateAvailable>('launcher-update-available', handler);
}

/** Bytes, while the launcher's own update downloads. */
export function onUpdateProgress(handler: (progress: UpdateProgress) => void): () => void {
  return subscribe<UpdateProgress>('launcher-update-progress', handler);
}

/**
 * The update is verified and the installer is starting.
 *
 * The last event this bridge will deliver. The process exits immediately after
 * it, and the installer relaunches the new one.
 */
export function onUpdateReady(handler: () => void): () => void {
  return subscribe<null>('launcher-update-ready', () => handler());
}

/**
 * No update was applied, and that is not a failure worth interrupting anybody
 * for.
 *
 * Fires for an unreachable feed, a signature that did not verify, and an
 * installer that would not run. All three mean the player is on the version
 * they already had, which works — so this belongs in a log or a quiet line in
 * settings, not in front of somebody trying to press Play.
 */
export function onUpdateFailed(handler: (event: UpdateFailed) => void): () => void {
  return subscribe<UpdateFailed>('launcher-update-failed', handler);
}
